<template>
<article>
    <p>
        Instrumenting a Python application with OpenTelemetry can provide valuable insights into the application's performance and behavior.
        OpenTelemetry is an open-source observability framework that enables the collection and exporting of telemetry data.
        This document covers the steps required to instrument a Python application with OpenTelemetry.
    </p>

    <h2 id="automatic">Auto-instrumentation</h2>

    <p>
        OpenTelemetry provides a Python agent that automatically detects and instruments the most popular
        application servers, clients, and frameworks.
        This means we don't even need to change the code of our app to instrument it.
    </p>

    <p>
        Let's start with a simple web application.
    </p>

    <v-tabs v-model="httpTab" class="mb-4" color="blue darken-2">
        <v-tab>Django</v-tab>
        <v-tab>FastAPI</v-tab>
        <v-tab>Flask</v-tab>
    </v-tabs>

    <v-tabs-items v-model="httpTab">
    <v-tab-item transition="scale">
            <p>A simple Django view handler:</p>

            <pre v-hl.python>
def hello(request, name):
    return HttpResponse("Hello, {}!".format(name))
            </pre>

            <p>Install OpenTelemetry dependencies:</p>

            <pre v-hl.bash>
pip install opentelemetry-distro opentelemetry-exporter-otlp
opentelemetry-bootstrap -a install
            </pre>

            <p>Then, run the application with the instrumentation:</p>

            <pre v-hl.bash>
export DJANGO_SETTINGS_MODULE=otel_django.settings \
  OTEL_RESOURCE_ATTRIBUTES="service.name=django-app" \
  OTEL_EXPORTER_OTLP_TRACES_ENDPOINT="http://coroot.coroot:8080/v1/traces" \
  OTEL_EXPORTER_OTLP_TRACES_PROTOCOL="http/protobuf" \
  && opentelemetry-instrument --traces_exporter otlp --metrics_exporter none ./manage.py runserver --noreload 8000
            </pre>

            <p>
                As a result, our app reports traces to the configured OpenTelemetry collector:

            </p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/django_trace.png" />
            </v-card>

            <p>Span attributes:</p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/django_server_span_attributes.png" />
            </v-card>
    </v-tab-item>
    <v-tab-item transition="scale">
            <p>A simple FastAPI app:</p>

            <pre v-hl.python>
from fastapi import FastAPI

app = FastAPI()

@app.get("/hello/{name}")
async def say_hello(name: str):
    return {"message": f"Hello, {name}!"}
            </pre>

            <p>Install OpenTelemetry dependencies:</p>

            <pre v-hl.bash>
pip install opentelemetry-distro opentelemetry-exporter-otlp
opentelemetry-bootstrap -a install
            </pre>

            <p>Then, run the application with the instrumentation:</p>

            <pre v-hl.bash>
export DJANGO_SETTINGS_MODULE=otel_django.settings \
  OTEL_RESOURCE_ATTRIBUTES="service.name=django-app" \
  OTEL_EXPORTER_OTLP_TRACES_ENDPOINT="http://coroot.coroot:8080/v1/traces" \
  OTEL_EXPORTER_OTLP_TRACES_PROTOCOL="http/protobuf" \
  && opentelemetry-instrument --traces_exporter otlp --metrics_exporter none uvicorn main:app
            </pre>

            <p>
                As a result, our app reports traces to the configured OpenTelemetry collector:
            </p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/fastapi_trace.png" />
            </v-card>

            <p>Span attributes:</p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/fastapi_server_span_attributes.png" />
            </v-card>
        </v-tab-item>
    <v-tab-item transition="scale">
            <p>A simple Flask app:</p>

            <pre v-hl.python>
from flask import Flask

app = Flask(__name__)

@app.route('/hello/&lt;name&gt;')
def hello(name):
    return 'Hello, {}!'.format(name)

if __name__ == '__main__':
    app.run()
            </pre>

            <p>Install OpenTelemetry dependencies:</p>

            <pre v-hl.bash>
pip install opentelemetry-distro opentelemetry-exporter-otlp
opentelemetry-bootstrap -a install
            </pre>

            <p>Then, run the application with the instrumentation:</p>

            <pre v-hl.bash>
export DJANGO_SETTINGS_MODULE=otel_django.settings \
  OTEL_RESOURCE_ATTRIBUTES="service.name=django-app" \
  OTEL_EXPORTER_OTLP_TRACES_ENDPOINT="http://coroot.coroot:8080/v1/traces" \
  OTEL_EXPORTER_OTLP_TRACES_PROTOCOL="http/protobuf" \
  && opentelemetry-instrument --traces_exporter otlp --metrics_exporter none flask run
            </pre>

            <p>
                As a result, our app reports traces to the configured OpenTelemetry collector:
            </p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/flask_trace.png" />
            </v-card>

            <p>Span attributes:</p>

            <v-card>
                <v-img eager src="/static/img/docs/coroot-ce/otel_python/flask_server_span_attributes.png" />
            </v-card>
        </v-tab-item>
    </v-tabs-items>

    <h2 id="exceptions">Exceptions</h2>

    <p>
        Now, let's explore what happens when our app raises an exception.
    </p>

    <pre v-hl.python>
def hello(request, name):
    raise Exception("Failure injection")
    return HttpResponse("Hello, {}!".format(name))
    </pre>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/error_trace.png" />
    </v-card>

    <p>
        The server span captures the exception and includes the corresponding traceback for better analysis and debugging:
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/error_span_attributes.png" />
    </v-card>

    <p>
        As you can see, we have easily identified the reason why this particular request resulted in an error.
    </p>

    <h2 id="database">Database calls</h2>

    <p>Now, let's add a database call to our app:</p>

    <pre v-hl.python>
from django.http import HttpResponse
from hello_app.models import Person


def hello(request, id):
    p = Person.objects.get(id=id)
    return HttpResponse("Hello, {}!".format(p.name))
    </pre>

    <p>
        Once again, there is no need to add any additional instrumentation as the OTel Python agent automatically captures
        every database call.
    </p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/trace_with_db_call.png" />
    </v-card>

    <p>Span attributes:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/db_call_span_attributes.png" />
    </v-card>

    <h2 id="http_calls">HTTP calls and context propagation</h2>

    <p>
        Next, instead of retrieving a user from the database, let's make an HTTP call to a service and retrieve a JSON
        response containing the desired information.
    </p>

    <pre v-hl.python>
import requests
from django.http import HttpResponse

def hello(request, id):
    r = requests.get('http://127.0.0.1:8082/user/{}'.format(id))
    name = r.json()['name']
    return HttpResponse("Hello, {}!".format(name))
    </pre>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/trace_with_http_call.png" />
    </v-card>

    <p>Client span attributes:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/http_call_span_attributes.png" />
    </v-card>

    <p>
        As you can see, the resulting trace includes a span reported by the <var>user</var> service.
        Both services are instrumented with OpenTelemetry.
        But how is the context of the current trace propagated between them?
        To gain a better understanding of context propagation, let's examine the request sent to the <var>user</var> service:
    </p>

    <pre v-hl.sh>
GET /user/2 HTTP/1.1
Host: 127.0.0.1
Connection: keep-alive
User-agent: python-requests/2.30.0
Accept: */*
Traceparent: 00-7d4ab2226954f6f712b8be0c067b21f6-b327da7466332edf-01
    </pre>

    <p>
        OpenTelemetry adds the <a href="https://www.w3.org/TR/trace-context/#traceparent-header" target="_blank">Traceparent</a>
        HTTP header on the client side, and dependency services read this header to propagate the trace context.
        It has the following format:
    </p>

    <p><var>Version-TraceID-ParentSpanID-TraceFlags</var></p>

    <p>
        In our case, <var>7d4ab2226954f6f712b8be0c067b21f6</var> is the TraceID, and <var>b327da7466332edf</var> is the ParentSpanId.
    </p>


    <h2 id="custom_attributes">Adding custom attributes and events to spans</h2>

    <p>
        If needed, you can retrieve the current span and set custom attributes or add events.
    </p>


    <pre v-hl.python>
import requests
from django.http import HttpResponse
from opentelemetry import trace

def hello(request, id):
    span = trace.get_current_span()

    # set an attribute
    span.set_attribute('user.id', id)

    r = requests.get('http://127.0.0.1:8082/user/{}'.format(id))

    # add an event
    span.add_event('the user profile has been loaded from the user service')

    name = r.json()['name']
    return HttpResponse("Hello, {}!".format(name))
    </pre>

    <p>The resulting span:</p>

    <v-card>
        <v-img eager src="/static/img/docs/coroot-ce/otel_python/custom_attributes_and_events.png" />
    </v-card>



</article>
</template>

<script>
export default {
    data() {
        return {
            httpTab: null,
        };
    },
}
</script>

<style scoped>

</style>
